import { faTrash, faUser, faUserPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { MultiValueGenericProps } from "react-select";
import { FormatOptionLabelMeta } from "react-select/dist/declarations/src/Select";
import HelpIcon from "../../../components/HelpIcon/HelpIcon";
import StyledSelect from "../../../components/StyledSelect/StyledSelect";
import Utils from "../../../components/Utils";
import api from "../../../utils/api";
import { EUserCategoryType, PostCategory, PRIVACY_ENUM, ROLES, UserCategory, UserOption } from "../../../utils/api/_type";
import { AUTH_METHOD, UserOptionColors } from "../../../utils/constantes";
import { useDebounce } from "../../../utils/utils";
import selectStyle from "../selectOptions.module.css";
import Button from "../../../components/Utils/Button/Button";
import Divider from "../../../components/Utils/Divider/Divider";
import AccessModal from "./Components/AccessModal/AccessModal";
import { UserContextType, useUser } from "../../../utils/context/User";

import style from "../RubriqueForm.module.css";

interface Props {
  value: PostCategory;
  setValue: React.Dispatch<React.SetStateAction<PostCategory>>;
  setShowUpdateMessage: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function Acces({ value, setValue, setShowUpdateMessage }: Props) {
  const [user] = useUser() as UserContextType;
  const [isAccessModalOpen, setIsAccessModalOpen] = useState(false);

  const queryClient = useQueryClient();
  const { mutate: updateCategory } = useMutation(api.categories.updateCategory, {
    onSuccess: (data) => {
      setShowUpdateMessage(true);

      setValue((b) => ({ ...b, ...data }));
    },
  });

  const { data: companyUsers } = useQuery("company_users", api.companies.getCompanyUsers);
  const { data: userCategories } = useQuery("user_categories", () => api.usercategories.getUserCategoriesByCategory(value.id as number), {
    enabled: !!value.id,
  });

  const notifiedUsers = useMemo(() => {
    return userCategories?.filter((uc) => uc.isNotified);
  }, [userCategories]);

  const [, setSelectedUsersFilterInput] = useState<UserOption[]>([]);
  const [selectedUsersFilter, setSelectedUsersFilter] = useDebounce<UserOption[]>(
    [],
    (selectedUsers) => {
      setSelectedUsersFilterInput(selectedUsers);
    },
    500
  );

  const { mutate: createUserCategory } = useMutation(api.usercategories.createUserCategory, {
    onSuccess: (data) => {
      queryClient.setQueryData<UserCategory[] | undefined>("user_categories", (old: UserCategory[] | undefined) => {
        if (!old) return undefined;
        if (old.find((uc) => uc.id === data.id)) {
          return old.map((uc) => (uc.id === data.id ? data : uc));
        }
        return [data, ...old];
      });
    },
  });

  const { mutate: updateUserCategory } = useMutation(api.usercategories.updateUserCategory, {
    onMutate: (variables) => {
      updateUserCategoryFromQueriesData(variables.userCategoryId, variables.body);
    },
  });

  function handleChangeAuthMethod(newVal: string) {
    setValue((b: any) => ({ ...b, authMethod: newVal }));

    if (value.id !== undefined) {
      updateCategory({
        id: value.id,
        body: { authMethod: newVal as AUTH_METHOD },
      });
    }
  }

  function handleCreateClick() {
    const selectedUsersFilterTemp: UserOption[] = structuredClone(selectedUsersFilter);
    setSelectedUsersFilter([]);
    if (value.id) {
      selectedUsersFilterTemp.forEach((suf) => {
        createUserCategory({ userId: suf.value, categoryId: value.id as number, body: { isNotified: true } });
      });
    } else {
      selectedUsersFilterTemp.forEach((suf) => {
        queryClient.setQueryData<UserCategory[] | undefined>("user_categories", (old: UserCategory[] | undefined) => {
          if (!old) old = [];

          old.unshift({
            id: suf.value,
            user: { id: suf.value, firstname: suf.label, lastname: suf.label },
            category: { id: -1 },
            type: EUserCategoryType.USER,
            isNotified: true,
            isGranted: false,
          });
          return old;
        });
      });
    }
  }

  function handleDeleteClick(userCategory: UserCategory) {
    if (value.id) {
      updateUserCategory({ userCategoryId: userCategory.id, body: { isNotified: false } });
    } else {
      updateUserCategoryFromQueriesData(userCategory.id, { isNotified: false });
    }
  }

  function updateUserCategoryFromQueriesData(id: number, body: { type?: EUserCategoryType; isNotified?: boolean }) {
    queryClient.setQueryData<UserCategory[] | undefined>("user_categories", (old: UserCategory[] | undefined) => {
      if (!old) return undefined;
      return old.map((uc) => (uc.id === id ? { ...uc, ...body } : uc));
    });
  }

  const formatOptionUser = (data: UserOption, formatOptionLabelMeta: FormatOptionLabelMeta<UserOption>) => {
    return (
      <div className={selectStyle.option}>
        <FontAwesomeIcon className={selectStyle.userIcon} icon={faUser} />
        <span style={{ marginLeft: "0.5rem" }}>{data.label}</span>
      </div>
    );
  };

  const MultiValueContainer = (props: MultiValueGenericProps<UserOption>) => {
    return (
      <div
        className={selectStyle.multiValueContainer}
        style={{
          backgroundColor: props.data.color,
        }}
      >
        {props.children}
      </div>
    );
  };

  return (
    <div
      className="onboarding-categories-5th"
      style={{
        width: "100%",
        maxWidth: "600px",
        marginLeft: "auto",
        marginRight: "auto",
        minHeight: "40vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div>
        <Utils.Select
          // helpIcon={faQuestionCircle}
          // helpContent="Cela vous permet de choisir de quelle manière des propositions peuvent être ajoutées à votre rubrique."
          label={
            <div style={{ display: "flex", alignItems: "center", marginBottom: "0.25rem" }}>
              <span style={{ fontWeight: "600" }}>Méthode d'authentification</span>
              <div style={{ position: "relative", marginLeft: "0.5rem" }}>
                <HelpIcon
                  title={"Type d'accès"}
                  content={"Cela vous permet de choisir de quelle manière des propositions peuvent être ajoutées à votre rubrique."}
                  iconSize="0.75rem"
                  containerSize="1.5rem"
                  width="30rem"
                  left="-15rem"
                  bottom="40px"
                />
              </div>
            </div>
          }
          value={value.authMethod}
          onChange={handleChangeAuthMethod}
        >
          {/* <option value="anything">Au choix</option> */}
          <option key="anonymous" value="anonymous">
            Anonyme
          </option>
          <option key="text" value="text">
            Libre: sans authentification
          </option>
          <option key="connection" value="connection">
            Connecté: nécessite un compte utilisateur
          </option>
        </Utils.Select>
      </div>

      <Divider variant="gray" />

      <div>
        <div className={style.switchContainer}>
          <Utils.Switch
            checked={!!value.noticeAnswer}
            onClick={() => {
              value.id && updateCategory({ id: value.id as number, body: { noticeAnswer: !value.noticeAnswer } });
              setValue((b) => ({ ...b, noticeAnswer: !value.noticeAnswer }));
            }}
          />
          <div className={style.switchLabel}>Notifier les utilisateurs en cas de réponse à leurs propositions</div>
        </div>

        <span style={{ fontWeight: 600 }}>Personnes à notifier en cas de propositions sur cette catégorie</span>
        <div style={{ display: "flex", alignItems: "center" }}>
          <StyledSelect
            options={
              companyUsers
                ? [
                    ...(companyUsers
                      ?.filter((u) => {
                        return !userCategories?.some((uc) => uc.user.id === u.id && uc.isNotified);
                      })
                      .map((u) => {
                        return {
                          value: u.id,
                          label: `${u.firstname} ${u.lastname}`,
                          color: UserOptionColors[Math.floor(Math.random() * UserOptionColors.length)],
                        };
                      }) || []),
                  ].flat()
                : undefined
            }
            className={selectStyle.searchSelect}
            value={selectedUsersFilter || []}
            isSearchable={true}
            onChange={(value) => {
              setSelectedUsersFilter(value as UserOption[]);
            }}
            isMulti
            closeMenuOnSelect={false}
            formatOptionLabel={formatOptionUser as any}
            components={{ MultiValueContainer } as any}
            noOptionsText={"Aucun utilisateur trouvé"}
          />
          <Utils.Button fullWidth={false} format="square" /*loading={isCreating}*/ onClick={() => handleCreateClick()}>
            Ajouter
          </Utils.Button>
        </div>
        <div className={style.accessGridHeaderContainer}>
          <div className={style.accessGridHeader}>Nom</div>
          <div className={style.accessGridHeader}>Prénom</div>
          <div className={style.accessGridHeader}></div>
          <div className={style.separator} />
        </div>
        <div className={style.accessGridContainer}>
          {notifiedUsers?.map((uc, index) => {
            return (
              <>
                {index !== 0 && <div className={style.separator} />}
                <div className={style.accessGridElement}>{uc.user.lastname}</div>
                <div className={style.accessGridElement}>{uc.user.firstname}</div>
                <div className={style.actionIconContainer}>
                  <div className={style.actionIcon} onClick={() => handleDeleteClick(uc)}>
                    <FontAwesomeIcon icon={faTrash} />
                  </div>
                </div>
              </>
            );
          })}
        </div>
      </div>

      {user?.roles.includes(ROLES.ROLE_COMPANY_ADMIN) ? (
        <>
          <Divider variant="gray" />
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                {value.privacy === PRIVACY_ENUM.PRIVATE ? (
                  <div style={{ color: "var(--warning)" }}>Les administrateurs sélectionnés peuvent gérer cette rubrique.</div>
                ) : (
                  <div style={{ color: "var(--gray)" }}>Tous les administrateurs peuvent gérer cette rubrique.</div>
                )}

                {value.isAccessPiloted ? (
                  <div style={{ color: "var(--warning)" }}>Les utilisateurs sélectionnés peuvent voir les réponses à cette rubrique.</div>
                ) : (
                  <div style={{ color: "var(--gray)" }}>Tous les utilisateurs peuvent voir les réponses à cette rubrique.</div>
                )}
              </div>
              <Button
                variant="gray"
                format="square"
                style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
                fullWidth={false}
                onClick={() => setIsAccessModalOpen(true)}
              >
                Gestion des permissions et de la visibité
                <FontAwesomeIcon icon={faUserPlus} />
              </Button>
            </div>
          </div>
        </>
      ) : null}

      <AccessModal
        isOpen={isAccessModalOpen}
        onClose={() => setIsAccessModalOpen(false)}
        category={value}
        setValue={setValue}
        updateCategory={updateCategory}
        createUserCategory={createUserCategory}
        updateUserCategory={updateUserCategory}
      />
    </div>
  );
}
